import { forwardRef, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { MediaType } from "../../graphql/generated";
import { authSelectors } from "../../store/auth/selector";
import styled from "../../styles";
import { InitialsAvatar } from "../Avatar";
import { SingleCheckBox } from "../Checkbox";
import { Button } from "../CTA";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { MediaItem } from "./types";

interface CommentFormProps {
  media: MediaItem;
  currentTime?: number;
  onAddComment?: (comment: string, timestamp?: number) => void;
  onFocus?: () => void;
  className?: string;
}

export const CommentForm = forwardRef<HTMLInputElement, CommentFormProps>(
  ({ media, currentTime, onAddComment, onFocus, className }, ref) => {
    const account = useSelector(authSelectors.account);
    const [commentText, setCommentText] = useState("");
    const [includeCommentTime, setIncludeCommentTime] = useState(false);

    const isVideo = media.mediaType === MediaType.Video;
    // Time is reflected via MediaPlayer onTimeUpdate prop in the dialog

    const handleSubmit = useCallback(() => {
      if (!commentText.trim() || !onAddComment) return;

      const currentMessage = commentText.trim();
      const timestampToSend =
        isVideo && includeCommentTime ? currentTime ?? 0 : undefined;

      onAddComment(currentMessage, timestampToSend);
      setCommentText("");
      setIncludeCommentTime(false);
    }, [commentText, includeCommentTime, isVideo, onAddComment, currentTime]);

    if (!onAddComment) {
      return null; // Don't render if comments are disabled
    }

    return (
      <CommentFormSection className={className}>
        <Flex align="flex-start" gap="s" margin="l 0 0">
          <InitialsAvatar
            name={`${account?.firstName || ""} ${account?.lastName || ""}`}
            size={26}
          />
          <CommentFormContainer onClick={onFocus}>
            <CommentFormInput
              ref={ref}
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              onFocus={onFocus}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <Flex align="center" justify="flex-end">
              <div>
                <Button
                  onClick={handleSubmit}
                  isDisabled={!commentText.trim()}
                  buttonType="primary"
                  size="xxs"
                >
                  Submit
                </Button>
              </div>
            </Flex>
          </CommentFormContainer>
        </Flex>

        {isVideo && (
          <Flex align="center" justify="flex-end">
            <Flex
              align="center"
              gap="xs"
              style={{ cursor: "pointer" }}
              onClick={() => setIncludeCommentTime(!includeCommentTime)}
              justify="flex-end"
              margin="s s 0 0"
            >
              <SingleCheckBox checked={includeCommentTime} size="s" />
              <Text size="xs" colorPreset="secondary" margin="0 0 0 xs">
                Comment at {formatTimestamp(currentTime ?? 0)}
              </Text>
            </Flex>
          </Flex>
        )}
      </CommentFormSection>
    );
  }
);

CommentForm.displayName = "CommentForm";

function formatTimestamp(time: number) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
}

const CommentFormSection = styled.div`
  margin-top: ${(p) => p.theme.spacing.l};
`;

const CommentFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  padding: ${(p) => p.theme.spacing.xs} ${(p) => p.theme.spacing.m}
    ${(p) => p.theme.spacing.s};
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${(p) => p.theme.color.typography.secondary}50;
  position: relative;
  background: ${(p) => p.theme.color.card.background};
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: ${(p) => p.theme.color.typography.text};
    outline: none;
  }

  &:focus-within {
    border-color: ${(p) => p.theme.color.primary};
    box-shadow: 0px 0px 0px 2px ${(p) => p.theme.color.input.hover};
    outline: none;
  }
`;

const CommentFormInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: ${(p) => p.theme.typography.size.s};
  color: ${(p) => p.theme.color.typography.text};
  background: transparent;
  padding: ${(p) => p.theme.spacing.xs} 0 ${(p) => p.theme.spacing.m};
`;
